import { useEffect, useRef, useState } from 'react'

/**
 * Ein Feld für eine Zahl – einmal gebaut, überall gleich.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Was vorher kaputt war
 * ────────────────────────────────────────────────────────────────────────────
 *
 * In den Belegmasken stand vierzehnmal dieselbe Zeile, sinngemäss:
 *
 *     value={z.unitPriceRp ? (z.unitPriceRp / 100).toFixed(2) : ''}
 *     onChange={(e) => setze({ unitPriceRp: zuRappen(e.target.value) })}
 *
 * Das liest sich harmlos und hat zwei Fehler, die beide gemeldet wurden:
 *
 * 1. Was im Feld steht, wird bei JEDEM Tastendruck neu aus der Zahl gerechnet.
 *    Wer «12.3» tippt, sieht «12.30», und der Textzeiger springt ans Ende.
 *    Wer weitertippt, hängt seine Ziffer hinter die Nachkommastellen: aus
 *    «12.30» wird «12.305», gerundet wieder «12.30» – die Eingabe verschwindet.
 *    Genau das ist mit «springt nach der zweiten Kommazahl» gemeint.
 *
 * 2. Wo statt der leeren Zeichenkette `String(wert)` stand, zeigte das Feld
 *    bei Null eine «0». Sie liess sich löschen – und war sofort wieder da,
 *    weil `Number('') || 0` erneut Null ergibt und die Anzeige daraus neu
 *    entsteht. Ein Feld, das den eigenen Inhalt zurückschreibt.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Die Regel dahinter
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Solange jemand tippt, gehört das Feld ihm. Der getippte Text bleibt Zeichen
 * für Zeichen stehen; gerechnet wird nebenher. Erst beim Verlassen räumt das
 * Feld auf und schreibt die Zahl sauber formatiert hin.
 *
 * Von aussen gesetzte Werte – ein Katalogpreis, der in die Zeile fährt –
 * kommen trotzdem an: das Feld folgt ihnen, solange der Textzeiger nicht
 * darin steht.
 *
 * Leer ist erlaubt und bedeutet Null. Eine Null, die man nicht wegbekommt,
 * ist keine Zahl, sondern ein Hindernis.
 */

/** «85.50» oder «85,50» → 85.5. Leer, Unsinn und ein einzelnes Minus → null. */
export function zahlAus(text: string): number | null {
  const roh = text.replace(/’|'/g, '').replace(',', '.').trim()
  if (roh === '' || roh === '-' || roh === '.' || roh === '-.') return null
  const n = Number(roh)
  return Number.isFinite(n) ? n : null
}

function alsText(wert: number, nachkomma: number, leerBeiNull: boolean): string {
  if (wert === 0 && leerBeiNull) return ''
  return nachkomma > 0 ? wert.toFixed(nachkomma) : String(wert)
}

export interface ZahlenfeldProps {
  id?: string
  /** Der Wert, wie ihn der Bildschirm führt. 0 zeigt ein leeres Feld. */
  wert: number
  aufWert: (wert: number) => void
  /** Nachkommastellen beim Aufräumen. 0 für Mengen und Prozente, 2 für Beträge. */
  nachkomma?: number
  /**
   * Untergrenze, die beim Verlassen greift – nicht beim Tippen.
   *
   * Für Zähler wie «nächste Rechnungsnummer», die es unter eins nicht gibt.
   * Beim Tippen wird nicht eingegriffen: ein Feld, das «1» zurückschreibt,
   * sobald man die 1 löscht, um 12 zu tippen, ist dasselbe Ärgernis wie die
   * Null, um die es hier geht.
   */
  mindestens?: number
  /**
   * Zeigt Null als leeres Feld. Für fast alles richtig – nicht für einen
   * Zähler, bei dem Null gar nicht vorkommt und leer nur verwirrt.
   */
  leerBeiNull?: boolean
  placeholder?: string
  className?: string
  disabled?: boolean
  'aria-label'?: string
}

export function Zahlenfeld({
  id,
  wert,
  aufWert,
  nachkomma = 0,
  mindestens,
  leerBeiNull = true,
  placeholder,
  className,
  disabled,
  'aria-label': ariaLabel
}: ZahlenfeldProps): JSX.Element {
  const [text, setText] = useState(() => alsText(wert, nachkomma, leerBeiNull))
  const imFeld = useRef(false)

  useEffect(() => {
    // Nur folgen, wenn niemand darin schreibt – sonst überschreibt ein
    // Neuzeichnen die halb getippte Zahl.
    if (!imFeld.current) setText(alsText(wert, nachkomma, leerBeiNull))
  }, [wert, nachkomma, leerBeiNull])

  return (
    <input
      autoComplete="off"
      id={id}
      className={className}
      disabled={disabled}
      aria-label={ariaLabel}
      /*
        inputMode statt type="number": type="number" bringt auf dem Desktop
        Pfeilchen mit, die beim Scrollen über dem Feld den Wert verstellen,
        und es weigert sich, einen halb getippten Wert wie «12,» überhaupt
        herauszugeben. Beides wollen wir hier nicht.
      */
      inputMode={nachkomma > 0 ? 'decimal' : 'numeric'}
      placeholder={placeholder}
      value={text}
      onFocus={() => {
        imFeld.current = true
      }}
      onChange={(e) => {
        // Nur wegwerfen, was in keiner Zahl vorkommt. Was übrig bleibt, steht
        // genau so im Feld – auch «12,» oder «0.», die beide auf dem Weg zu
        // einer gültigen Zahl liegen.
        const gefiltert = e.target.value.replace(/[^\d.,\-]/g, '')
        setText(gefiltert)
        aufWert(zahlAus(gefiltert) ?? 0)
      }}
      onBlur={() => {
        imFeld.current = false
        const roh = zahlAus(text) ?? 0
        const n = mindestens !== undefined && roh < mindestens ? mindestens : roh
        setText(alsText(n, nachkomma, leerBeiNull))
        aufWert(n)
      }}
    />
  )
}

/**
 * Dasselbe Feld für Geld – nach aussen in Rappen.
 *
 * Rappen und nicht Franken, weil alles dahinter in Rappen rechnet. Die
 * Umrechnung steht hier, damit sie nicht an acht Stellen mit acht leicht
 * verschiedenen Rundungen steht.
 */
export function Betragsfeld({
  id,
  rappen,
  aufRappen,
  placeholder = '0.00',
  className,
  disabled,
  'aria-label': ariaLabel
}: {
  id?: string
  rappen: number
  aufRappen: (rappen: number) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  'aria-label'?: string
}): JSX.Element {
  return (
    <Zahlenfeld
      id={id}
      className={className}
      disabled={disabled}
      aria-label={ariaLabel}
      nachkomma={2}
      placeholder={placeholder}
      wert={rappen / 100}
      aufWert={(franken) => aufRappen(Math.round(franken * 100))}
    />
  )
}
